import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FAQPage() {
  const { t, language } = useLanguage();
  const { logoUrl } = useLogo();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/faq?lang=${language}`).then(res => setItems(res.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [language]);

  const toggle = (idx) => setOpenIdx(openIdx === idx ? null : idx);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2"><img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" /><span className="font-logo text-2xl font-bold text-primary">PRC Remit</span></Link>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Link to="/"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" />{t('company_back')}</Button></Link>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="faq-page">
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="h-8 w-8 text-accent" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">{t('faq_page_title')}</h1>
          <p className="text-muted-foreground text-lg">{t('faq_page_subtitle')}</p>
        </div>
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
            <HelpCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-muted-foreground">{t('legal_no_data')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={idx} className="bg-card border border-border/50 rounded-xl overflow-hidden" data-testid={`faq-item-${idx}`}>
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-secondary/20 smooth-transition"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-accent bg-accent/10 px-2 py-0.5 rounded flex-shrink-0">Q{idx + 1}</span>
                    <span className="font-medium text-primary">{item.question}</span>
                  </div>
                  {openIdx === idx ? <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
                </button>
                {openIdx === idx && (
                  <div className="px-6 pb-5 border-t border-border/50">
                    <div className="pt-4 text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid={`faq-answer-${idx}`}>
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <footer className="border-t border-border bg-secondary/30 py-8 mt-12"><div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-sm text-muted-foreground"><p>&copy; 2026 PRC Remit. All rights reserved.</p></div></footer>
    </div>
  );
}
